import { CreateTick, PartnerDetailEdit as PartnerDetailEditIcon } from 'common/components/svgs';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Breadcrumb, Empty, Spin, Switch, Table, Tabs, Tag, Tooltip, message } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrentRoles } from '~components/switch-role/useSwitchRole';
import { PartnerType } from '~constants/partner';
import { formatPriceWithSpace } from '~helpers/currency';
import PartnerRegisterCityEditor from '~pages/dashboard/referral/partner/partner-detail/partner-register-city-editor';
import {
  MUTATION_UPDATE_INDIVIDUAL_PARTNER_CONTACT,
  QUERY_PARTNER_CONTACT,
} from '~pages/dashboard/referral/partner/queries';
import generatePath from '~settings/routing';
import { capitalizeFirstLetter } from '~helpers/jstool';

import PartnerCommissionPlanEditor from './partner-commission-plan-editor';

const PartnerContactCertificationStatus = {
  INCOMPLETE: 'INCOMPLETE',
  SUCCESS: 'SUCCEED',
  FAILED: 'FAIL',
};

const PartnerContactSignStatus = {
  INCOMPLETE: 'INCOMPLETE',
  INPROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCEED',
  FAILED: 'FAIL',
};

const PartnerDetail = ({ id }) => {
  const { t } = useTranslation();
  const { role } = useCurrentRoles();
  const [selectedTab, setSelectedTab] = useState('commission');
  const [showPartnerRegisterCityEditor, setShowPartnerRegisterCityEditor] = useState(false);
  const [partnerRegisterCityEditorParams, setPartnerRegisterCityEditorParams] = useState(null);

  const { data: partnerContactsResponse, loading: partnerContactsLoading } = useQuery(
    QUERY_PARTNER_CONTACT,
    {
      variables: {
        partnerCode: id,
      },
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  const [updatePartner] = useMutation(MUTATION_UPDATE_INDIVIDUAL_PARTNER_CONTACT, {
    refetchQueries: [QUERY_PARTNER_CONTACT],
    onError: e => {
      message.error(e.message);
    },
  });

  const partnerNode = useMemo(() => {
    return partnerContactsResponse?.partnerContacts?.edges[0]?.node;
  }, [partnerContactsResponse]);

  const handleTableChange = key => {
    setSelectedTab(key);
  };

  const certificationStatus = useMemo(() => {
    return Object.keys(PartnerContactCertificationStatus)[
      Object.values(PartnerContactCertificationStatus).indexOf(
        partnerNode?.certificationSign?.certificationStatus || 'INCOMPLETE',
      )
    ];
  }, [partnerNode]);

  const signStatus = useMemo(() => {
    return Object.keys(PartnerContactSignStatus)[
      Object.values(PartnerContactSignStatus).indexOf(
        partnerNode?.certificationSign?.signStatus || 'INCOMPLETE',
      )
    ];
  }, [partnerNode]);

  const referrerName = useMemo(() => {
    const firstName = partnerNode?.partner?.internalReferrer?.firstName;
    const lastName = partnerNode?.partner?.internalReferrer?.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName || lastName) {
      return firstName || lastName;
    }
    if (!firstName && !lastName) {
      return 'None';
    }
  }, [partnerNode]);

  const handleEditRegisterCity = useCallback((partnerId, currentCityId, partnerCountrySlug) => {
    setShowPartnerRegisterCityEditor(true);
    setPartnerRegisterCityEditorParams({
      partnerId: partnerId,
      currentCityId: currentCityId,
      partnerCountrySlug: partnerCountrySlug,
    });
  }, []);

  const handleClosePartnerRegisterCityEditorModal = useCallback(() => {
    setShowPartnerRegisterCityEditor(false);
  }, []);

  async function handleSwitchSelfCrateLeadsAbility(partnerId, enable) {
    let response;
    if (enable) {
      response = await updatePartner({
        variables: { id: partnerId, referralLead: enable },
      });
    } else {
      response = await updatePartner({
        variables: { id: partnerId, referralLead: enable, agentMode: false },
      });
    }

    if (response?.data?.updateIndividualPartnerContact?.result) {
      message.success(
        t('cms.referral.partner.detail.editor.action.self_create_leads.toast.success'),
      );
    }
  }

  async function handleSwitchBookOnBehalf(partnerId, enable) {
    const response = await updatePartner({
      variables: { id: partnerId, agentMode: enable },
    });
    if (response?.data?.updateIndividualPartnerContact?.result) {
      message.success(t('cms.referral.partner.detail.editor.action.book_on_behalf.toast.success'));
    }
  }

  return (
    <div className="partner-details">
      <Choose>
        <When condition={partnerContactsLoading || !partnerNode}>
          <div className="partner-details__loading">
            <Spin />
          </div>
        </When>
        <Otherwise>
          <div className="partner-details__header">
            <div className="partner-details__bread-crumb">
              <Breadcrumb>
                <Breadcrumb.Item>
                  <Link
                    className="partner-details__bread-crumb-text"
                    to={generatePath('referral.partner', {})}
                  >
                    {t('cms.referral.partner.detail.header.menu_link.partner_list')}
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <span className="partner-details__bread-crumb-text">
                    {partnerNode.partner.name}
                  </span>
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <h2 className="partner-details__title">{partnerNode.partner.name}</h2>

            <div className="partner-details__header-content">
              <div className="partner-details__info-row partner-details__flex-space-between">
                <div className="partner-details__info-row">
                  <div className="partner-details__info-col">
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.country')}: ${
                        partnerNode.partner.originCountry?.name ||
                        t('cms.referral.partner.list.column.register_city.blank')
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.type')}: ${
                        PartnerType[partnerNode.partner.type] || '-'
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.certification_status')}: ${t(
                        `cms.referral.partner.list.column.certification_status.${certificationStatus.toLowerCase()}`,
                      )}`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.number')}: ${
                        partnerNode.phoneNumber || '-'
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.sign_status')}: ${t(
                        `cms.referral.partner.list.column.sign_status.${signStatus.toLowerCase()}`,
                      )}`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.referral_lead')}`}
                      <Switch
                        className="partner-details__info__switch"
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        disabled={true}
                        checked={partnerNode.referralLead}
                        onChange={checked =>
                          handleSwitchSelfCrateLeadsAbility(partnerNode.id, checked)
                        }
                      />
                    </span>
                  </div>
                  <div className="partner-details__info-col">
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.register_city')}: `}
                      <span>
                        {partnerNode.city?.name ||
                          t('cms.referral.partner.list.column.register_city.blank')}
                      </span>
                      <span
                        className="partner-details__info-col__link"
                        onClick={() =>
                          handleEditRegisterCity(
                            partnerNode.id,
                            partnerNode.city?.id,
                            partnerNode.partner.originCountry?.slug,
                          )
                        }
                      >
                        {t('cms.referral.partner.list.column.actions.edit')}
                      </span>
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.partner_code')}: ${
                        partnerNode.partner.code
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.referrer_name')}: ${referrerName}`}
                    </span>

                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.email')}: ${
                        partnerNode.emailAddress || '-'
                      }`}
                    </span>

                    <span className="partner-details__info">
                      {`${t('cms.referral.payout.list.column.payment_preference')}: ${
                        capitalizeFirstLetter(partnerNode.partner.paymentPreference) || '-'
                      }`}
                    </span>

                    <If condition={partnerNode.referralLead}>
                      <span className="partner-details__info">
                        {`${t('cms.referral.partner.list.column.book_on_behalf_of_student')}`}
                        <Tooltip
                          title={t(
                            'cms.referral.partner.list.column.book_on_behalf_of_student.tips',
                          )}
                        >
                          <QuestionCircleOutlined
                            style={{
                              margin: '0 2px 0',
                            }}
                          />
                        </Tooltip>
                        {': '}
                        <Switch
                          className="partner-details__info__switch"
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          checked={partnerNode.agentMode}
                          onChange={checked => handleSwitchBookOnBehalf(partnerNode.id, checked)}
                        />
                      </span>
                    </If>
                  </div>
                </div>
                <div className="partner-details__info-row">
                  <div className="partner-details__info-col">
                    <span className="partner-details__info">
                      <p className="partner-details__info__wallet">
                        {t('cms.referral.partner.list.column.account_balance')}
                      </p>
                      <p className="partner-details__info__wallet__amount">
                        {formatPriceWithSpace(
                          partnerNode.partner.withdrawAmountAggregation?.remainWithdraw,
                          partnerNode.partner.withdrawAmountAggregation?.currency,
                        )}
                      </p>
                    </span>
                  </div>
                </div>
              </div>

              <Tabs
                className="partner-details__tabs"
                activeKey={selectedTab}
                onChange={handleTableChange}
              >
                <Tabs.TabPane
                  tab={t('cms.referral.partner.detail.header.tabs.commission')}
                  key="commission"
                />
                <Tabs.TabPane
                  tab={t('cms.referral.partner.detail.header.tabs.payout')}
                  key="payout"
                />
              </Tabs>
            </div>
          </div>
          <div className="">
            <Choose>
              <When condition={selectedTab === 'commission'}>
                <Choose>
                  <When
                    condition={
                      partnerNode.partner.commissionPlan && partnerNode.partner.currentTier
                    }
                  >
                    <CommissionCard
                      commission={partnerNode.partner.commissionPlan}
                      currentTierId={partnerNode.partner.currentTierId}
                      currentTierName={partnerNode.partner.currentTier.name}
                      partnerId={partnerNode.partner.id}
                      countryCode={partnerNode.partner.originCountry.countryCode}
                      role={role}
                    />
                  </When>
                  <Otherwise>
                    <div className="partner-detail-tab-card partner-detail-tab-card__body">
                      <Empty />
                    </div>
                  </Otherwise>
                </Choose>
              </When>
              <When condition={selectedTab === 'payout'}>
                <Choose>
                  <When condition={partnerNode.partner.payoutPlan}>
                    <PayoutPlanCard plan={partnerNode.partner.payoutPlan} />
                  </When>
                  <Otherwise>
                    <div className="partner-detail-tab-card partner-detail-tab-card__body">
                      <Empty />
                    </div>
                  </Otherwise>
                </Choose>
              </When>
            </Choose>
          </div>

          <If condition={showPartnerRegisterCityEditor}>
            <PartnerRegisterCityEditor
              activeModal
              t={t}
              editParams={partnerRegisterCityEditorParams}
              handleCloseModal={handleClosePartnerRegisterCityEditorModal}
            />
          </If>
        </Otherwise>
      </Choose>
    </div>
  );
};

const CommissionCard = ({
  commission,
  currentTierId,
  partnerId,
  countryCode,
  currentTierName,
  role,
}) => {
  const { t } = useTranslation();
  const [showPartnerCommissionPlanEditor, setShowPartnerCommissionPlanEditor] = useState(false);
  const [editParams, setEditParams] = useState({
    partnerId: null,
    partnerCountryCode: null,
    currentCommissionId: null,
  });

  const handleEdit = useCallback((partnerId, partnerCountryCode, commissionPlanId) => {
    setShowPartnerCommissionPlanEditor(true);
    setEditParams({
      partnerId: partnerId,
      partnerCountryCode: partnerCountryCode,
      currentCommissionId: commissionPlanId,
    });
  }, []);

  const handleClosePartnerCommissionPlanEditorModal = useCallback(() => {
    setShowPartnerCommissionPlanEditor(false);
  }, []);

  const tierCalculationType = commission?.tiers[0]?.calculationRules[0]?.calculationType;

  return (
    <>
      <div className="partner-detail-tab-card">
        <div className="partner-detail-tab-card__header">
          <div className="partner-detail-tab-card__header__title">
            {t('cms.referral.partner.detail.tab.commission.title')}
          </div>
          <If condition={role !== 'bd'}>
            <PartnerDetailEditIcon
              onClick={() => handleEdit(partnerId, countryCode, commission.id)}
              className="partner-detail-tab-card__header__icon"
            />
          </If>
        </div>
        <div className="partner-detail-tab-card__body">
          <div className="partner-detail-tab-card__custom-table">
            <div className="partner-detail-tab-card__custom-table__header">
              <div className="partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--w-250 partner-detail-tab-card__custom-table--flex-shrink-0">
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__list__column--divider partner-detail-tab-card__custom-table--w-full">
                  {t('cms.referral.partner.detail.tab.commission.table.header.name')}
                </div>
              </div>
              <div className="partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--w-full">
                <div className="partner-detail-tab-card__custom-table--w-52" />
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-226">
                  {t('cms.referral.partner.detail.tab.commission.table.header.bookingsNo')}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.longTerm', {
                    currency: tierCalculationType === 'PERCENTAGE' ? '%' : commission.currency,
                  })}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.shortTerm', {
                    currency: tierCalculationType === 'PERCENTAGE' ? '%' : commission.currency,
                  })}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.tierName')}
                </div>
              </div>
            </div>
            <div className="partner-detail-tab-card__custom-table__body">
              <div className="partner-detail-tab-card__custom-table__left partner-detail-tab-card__custom-table--w-250">
                <div className="partner-detail-tab-card__custom-table__column-body">
                  {commission.name}（{currentTierName}）
                  {commission.default ? (
                    <Tag className="partner-detail-tab-card__default-tag">
                      {t('cms.referral.commission_plan.list_card.tag.default')}
                    </Tag>
                  ) : null}
                </div>
              </div>
              <div className="partner-detail-tab-card__custom-table__right">
                {commission.tiers.map((tier, tierIndex) => (
                  <div
                    key={tier.name}
                    className={classNames('partner-detail-tab-card__custom-table__row', {
                      'partner-detail-tab-card__custom-table__row--active':
                        currentTierId === tierIndex,
                    })}
                  >
                    <div className="partner-detail-tab-card__custom-table__select-icon-column partner-detail-tab-card__custom-table--w-52 partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--flex-center">
                      {currentTierId === tierIndex ? (
                        <CreateTick width="16" height="12" fill="#38B2A6" />
                      ) : null}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-226">
                      {tierIndex === commission.tiers.length - 1
                        ? `≥ ${tier.bookingCountFrom}`
                        : `${tier.bookingCountFrom} - ${tier.bookingCountTo}`}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.longTermCalculationValue}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.shortTermCalculationValue}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <If condition={showPartnerCommissionPlanEditor}>
        <PartnerCommissionPlanEditor
          activeModal
          t={t}
          editParams={editParams}
          handleCloseModal={handleClosePartnerCommissionPlanEditorModal}
        />
      </If>
    </>
  );
};

const PayoutPlanCard = ({ plan }) => {
  const { t } = useTranslation();
  const tableColumns = useMemo(() => {
    return [
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t('cms.referral.partner.detail.tab.payout.table.header.name')}
          </div>
        ),
        dataIndex: 'name',
        key: 'name',
        // width: 140,
        className: 'partner-detail-tab-card__list__column',
        render: (text, record) => (
          <span className="partner-detail-tab-card__list__column-body">
            {text}
            {record.default ? (
              <Tag className="partner-detail-tab-card__default-tag">
                {t('cms.referral.commission_plan.list_card.tag.default')}
              </Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t(
              'cms.referral.partner.detail.tab.payout.table.header.percentagePaidAfterOppsCompleted',
            )}
          </div>
        ),
        dataIndex: 'bookingCompletedPercent',
        key: 'bookingCompletedPercent',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body">{`${
            text ? text + '%' : '-'
          }`}</span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t(
              'cms.referral.partner.detail.tab.payout.table.header.percentagePaidAfterLandlordCommissionPaid',
            )}
          </div>
        ),
        dataIndex: 'bookingPaidPercent',
        key: 'bookingPaidPercent',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body">{`${
            text ? text + '%' : '-'
          }`}</span>
        ),
      },
    ];
  }, []);

  return (
    <div className="partner-detail-tab-card">
      <div className="partner-detail-tab-card__header">
        <div className="partner-detail-tab-card__header__title">Payout Plan</div>
      </div>
      <div className="partner-detail-tab-card__body">
        <Table
          columns={tableColumns}
          size="middle"
          className="table-list partner-detail-tab-card__table"
          pagination={false}
          dataSource={[plan]}
        />
      </div>
    </div>
  );
};

export default PartnerDetail;
